/**
 * DeviceSlotService — Requests a device slot from the backend.
 *
 * Device slots (0-99) are allocated per-device per-store to ensure
 * collision-proof invoice number generation even when fully offline.
 *
 * Desktop port of BigxBookApp/src/services/DeviceSlotService.ts. Uses the
 * same idempotent GET against /sync/pull?_slotOnly=true so multiple desktops
 * for the same merchant get distinct, stable slots.
 */

import { API_URL } from '@/config';
import { useAppStore } from '@/store/useAppStore';

const DEVICE_SLOT_TIMEOUT_MS = 8000;

export async function requestDeviceSlot(
  deviceId: string,
  storeId: string,
): Promise<number | null> {
  const { authToken, setDeviceSlot } = useAppStore.getState();
  if (!authToken) return null;

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), DEVICE_SLOT_TIMEOUT_MS);

    const response = await fetch(
      `${API_URL}/sync/pull?lastPulledAt=0&storeId=${encodeURIComponent(storeId)}&deviceId=${encodeURIComponent(deviceId)}&_slotOnly=true`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      },
    ).finally(() => clearTimeout(timer));

    if (!response.ok) return null;

    const data = await response.json();
    if (data.deviceSlot != null) {
      await setDeviceSlot(data.deviceSlot);
      return data.deviceSlot as number;
    }

    return null;
  } catch (e) {
    console.warn('[DeviceSlotService] Failed to request slot:', (e as Error).message);
    return null;
  }
}

/**
 * Best-effort slot pre-allocation. Call when the user signs in (or on app
 * resume) so the first sale doesn't fall back to a hash-based slot.
 */
export async function ensureDeviceSlot(): Promise<number | null> {
  const { activeStoreId, deviceId, deviceSlot } = useAppStore.getState();
  if (deviceSlot != null) return deviceSlot;
  if (!activeStoreId || !deviceId) return null;
  return requestDeviceSlot(deviceId, activeStoreId);
}
